import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import SEO from '../components/SEO';

const Profile = () => {
  const { currentUser, getUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    }
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingCommunications: true
  });

  useEffect(() => {
    if (currentUser) {
      loadUserData();
    }
  }, [currentUser]);

  const loadUserData = async () => {
    try {
      // Use Firestore data from the enhanced user object or fetch fresh data
      const userData = currentUser.firestoreData || await getUserData(currentUser.uid);
      
      if (userData) {
        setProfileData({
          firstName: userData.firstName || currentUser.firstName || '',
          lastName: userData.lastName || currentUser.lastName || '',
          email: userData.email || currentUser.email || '',
          phone: userData.phone || currentUser.phone || '',
          dateOfBirth: userData.dateOfBirth || '',
          address: {
            street: userData.address?.street || currentUser.address?.street || '',
            city: userData.address?.city || currentUser.address?.city || '',
            state: userData.address?.state || currentUser.address?.state || '',
            zipCode: userData.address?.zipCode || currentUser.address?.zipCode || '',
            country: userData.address?.country || currentUser.address?.country || ''
          }
        });
        
        // Load user preferences
        setPreferences({
          emailNotifications: userData.preferences?.emailNotifications ?? currentUser.preferences?.emailNotifications ?? true,
          smsNotifications: userData.preferences?.smsNotifications ?? currentUser.preferences?.smsNotifications ?? false,
          marketingCommunications: userData.preferences?.marketingCommunications ?? currentUser.preferences?.marketingCommunications ?? true
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setProfileData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePreferenceChange = async (preferenceName, value) => {
    try {
      // Update local state
      setPreferences(prev => ({
        ...prev,
        [preferenceName]: value
      }));

      // Update Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        [`preferences.${preferenceName}`]: value,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Preference updated in Firestore:', {
        uid: currentUser.uid,
        preference: preferenceName,
        value: value
      });
    } catch (error) {
      console.error('Error updating preference:', error);
      // Revert local state on error
      setPreferences(prev => ({
        ...prev,
        [preferenceName]: !value
      }));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Ensure we have a valid current user from Firebase Auth
      if (!currentUser || !currentUser.uid) {
        throw new Error('No authenticated user found');
      }

      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        displayName: `${profileData.firstName} ${profileData.lastName}`
      });

      // Update email if changed (this requires recent authentication)
      if (profileData.email !== currentUser.email) {
        try {
          await updateEmail(currentUser, profileData.email);
        } catch (emailError) {
          console.warn('Email update failed, continuing with other updates:', emailError);
          // Continue with other updates even if email update fails
          setMessage('Profile updated successfully! Note: Email update may require re-authentication.');
        }
      }

      // Update Firestore document with comprehensive data
      await updateDoc(doc(db, 'users', currentUser.uid), {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email.toLowerCase(),
        phone: profileData.phone,
        dateOfBirth: profileData.dateOfBirth,
        address: profileData.address,
        displayName: `${profileData.firstName} ${profileData.lastName}`,
        profile: {
          isComplete: true,
          lastUpdated: new Date().toISOString()
        },
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ Profile data successfully updated in Firestore:', {
        uid: currentUser.uid,
        name: `${profileData.firstName} ${profileData.lastName}`,
        email: profileData.email
      });

      if (!message) {
        setMessage('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in to update your email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email address is already in use by another account.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      // Ensure we have a valid current user from Firebase Auth
      if (!currentUser || !currentUser.uid) {
        throw new Error('No authenticated user found');
      }

      await updatePassword(currentUser, passwordData.newPassword);
      setMessage('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password update error:', error);
      let errorMessage = 'Failed to update password. Please try again.';
      
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in to change your password.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <ProfileContainer>
        <LoadingMessage>Please log in to view your profile.</LoadingMessage>
      </ProfileContainer>
    );
  }

  return (
    <>
      <SEO
        title="Profile - Nike 3D Store"
        description="Manage your Nike 3D Store profile, update personal information, and account settings."
        keywords="Nike profile, account settings, personal information, user profile"
        canonicalUrl="https://nike3d.com/profile"
      />
      <ProfileContainer>
        <ProfileHeader>
          <HeaderContent>
            <UserAvatar>
              {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
            </UserAvatar>
            <UserInfo>
              <UserName>{currentUser.displayName || 'User'}</UserName>
              <UserEmail>{currentUser.email}</UserEmail>
              <MemberSince>
                Member since {new Date(currentUser.metadata.creationTime).toLocaleDateString()}
              </MemberSince>
            </UserInfo>
          </HeaderContent>
        </ProfileHeader>

        <ProfileContent>
          <TabNavigation>
            <Tab 
              $active={activeTab === 'profile'} 
              onClick={() => setActiveTab('profile')}
            >
              Personal Information
            </Tab>
            <Tab 
              $active={activeTab === 'security'} 
              onClick={() => setActiveTab('security')}
            >
              Security
            </Tab>
            <Tab 
              $active={activeTab === 'preferences'} 
              onClick={() => setActiveTab('preferences')}
            >
              Preferences
            </Tab>
          </TabNavigation>

          {message && <SuccessMessage>{message}</SuccessMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {activeTab === 'profile' && (
            <TabContent>
              <SectionTitle>Personal Information</SectionTitle>
              <ProfileForm onSubmit={handleProfileSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label>First Name</Label>
                    <Input
                      type="text"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name</Label>
                    <Input
                      type="text"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleProfileChange}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    name="dateOfBirth"
                    value={profileData.dateOfBirth}
                    onChange={handleProfileChange}
                  />
                </FormGroup>

                <SectionDivider />
                <SectionSubtitle>Address Information</SectionSubtitle>

                <FormGroup>
                  <Label>Street Address</Label>
                  <Input
                    type="text"
                    name="address.street"
                    value={profileData.address.street}
                    onChange={handleProfileChange}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      type="text"
                      name="address.city"
                      value={profileData.address.city}
                      onChange={handleProfileChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>State</Label>
                    <Input
                      type="text"
                      name="address.state"
                      value={profileData.address.state}
                      onChange={handleProfileChange}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>ZIP Code</Label>
                    <Input
                      type="text"
                      name="address.zipCode"
                      value={profileData.address.zipCode}
                      onChange={handleProfileChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Country</Label>
                    <Input
                      type="text"
                      name="address.country"
                      value={profileData.address.country}
                      onChange={handleProfileChange}
                    />
                  </FormGroup>
                </FormRow>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Profile'}
                </SubmitButton>
              </ProfileForm>
            </TabContent>
          )}

          {activeTab === 'security' && (
            <TabContent>
              <SectionTitle>Change Password</SectionTitle>
              <ProfileForm onSubmit={handlePasswordSubmit}>
                <FormGroup>
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Password'}
                </SubmitButton>
              </ProfileForm>
            </TabContent>
          )}

          {activeTab === 'preferences' && (
            <TabContent>
              <SectionTitle>Account Preferences</SectionTitle>
              <PreferenceItem>
                <div>
                  <PreferenceLabel>Email Notifications</PreferenceLabel>
                  <PreferenceDescription>Receive emails about new products and offers</PreferenceDescription>
                </div>
                <Toggle>
                  <input 
                    type="checkbox" 
                    checked={preferences.emailNotifications}
                    onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </PreferenceItem>

              <PreferenceItem>
                <div>
                  <PreferenceLabel>SMS Notifications</PreferenceLabel>
                  <PreferenceDescription>Receive SMS updates about your orders</PreferenceDescription>
                </div>
                <Toggle>
                  <input 
                    type="checkbox" 
                    checked={preferences.smsNotifications}
                    onChange={(e) => handlePreferenceChange('smsNotifications', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </PreferenceItem>

              <PreferenceItem>
                <div>
                  <PreferenceLabel>Marketing Communications</PreferenceLabel>
                  <PreferenceDescription>Receive marketing emails and promotional content</PreferenceDescription>
                </div>
                <Toggle>
                  <input 
                    type="checkbox" 
                    checked={preferences.marketingCommunications}
                    onChange={(e) => handlePreferenceChange('marketingCommunications', e.target.checked)}
                  />
                  <span className="slider"></span>
                </Toggle>
              </PreferenceItem>
            </TabContent>
          )}
        </ProfileContent>
      </ProfileContainer>
    </>
  );
};

export default Profile;

const ProfileContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50vh;
  font-size: 18px;
  color: #666;
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #111 0%, #333 100%);
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 30px;
  color: white;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
`;

const UserAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fff;
  color: #111;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const UserEmail = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin: 0 0 4px 0;
`;

const MemberSince = styled.p`
  font-size: 14px;
  opacity: 0.7;
  margin: 0;
`;

const ProfileContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid #e9ecef;
  
  @media (max-width: 768px) {
    overflow-x: auto;
  }
`;

const Tab = styled.button`
  padding: 20px 30px;
  border: none;
  background: ${props => props.$active ? '#fff' : 'transparent'};
  color: ${props => props.$active ? '#111' : '#666'};
  font-weight: ${props => props.$active ? '600' : '400'};
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid ${props => props.$active ? '#111' : 'transparent'};
  white-space: nowrap;
  
  &:hover {
    background: #f8f9fa;
    color: #111;
  }
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    font-size: 14px;
  }
`;

const TabContent = styled.div`
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111;
  margin: 0 0 30px 0;
`;

const SectionSubtitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: #e9ecef;
  margin: 30px 0 20px 0;
`;

const ProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  background: #111;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
  
  &:hover:not(:disabled) {
    background: #000;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const PreferenceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const PreferenceLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const PreferenceDescription = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 4px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 34px;
    
    &:before {
      position: absolute;
      content: "";
      height: 26px;
      width: 26px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
    }
  }
  
  input:checked + .slider {
    background-color: #111;
  }
  
  input:checked + .slider:before {
    transform: translateX(26px);
  }
`;
